// The waveform host (P2.6) — the main-thread side of waveform-worker.entry.ts.
//
// Same two-path shape as `render-host.ts`, and for PRD §6.3's reason rather than symmetry: "no
// feature may crash Tier 2; it degrades or hides". A browser without OffscreenCanvas still gets a
// waveform — it just draws on this thread. Both paths run the *same* `WaveformRenderer` and the
// same `RenderLoop` and write the same `RENDER_STATS`, which is what makes the fallback a fallback
// rather than a second implementation nobody develops against.

import {
  RENDER_STATS_BYTES,
  type RenderPath,
  type RenderWorkerEvent,
} from '../../platform/types/render';
import type {
  WaveformGrid,
  WaveformOrientation,
  WaveformTheme,
  WaveformWorkerMessage,
} from '../../platform/types/waveform-render';
import type { SignalSourceIndex } from '../../platform/types/rings';
import { RenderLoop } from '../core/render-loop';
import { supportsOffscreenCanvas } from '../core/render-host';
import { WaveformRenderer } from './waveform-renderer';

/**
 * Where the waveform worker's bundle lives — a stable URL, the same in dev and production.
 *
 * Not `new Worker(new URL(…, import.meta.url))`, for the reason P0.8 paid to learn with the render
 * worker: that spelling makes Vite emit its own chunk, so the bundle guard and the payload budget
 * end up measuring a copy nobody loads. A guard pointed at a shadow is not a guard.
 */
export const WAVEFORM_WORKER_URL = '/waveform-worker.js';

/**
 * Everything a lane is born with (P3.13).
 *
 * **An object rather than more positional parameters**, the same call the loader hook made for
 * `TrackLoaderDeps`: this was six arguments and P3.13 needs eight, at which point a call site is a
 * row of unlabelled values and a transposed pair of them is a silent bug — a lane drawing deck B's
 * playhead under deck A's waveform would look almost right.
 */
export interface WaveformHostOptions {
  readonly canvas: HTMLCanvasElement;
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
  readonly theme: WaveformTheme;
  /** Which deck's block in the FR-26 frame this lane's playhead comes from. */
  readonly sourceIndex: SignalSourceIndex;
  /** Deck A draws mirrored so the two ribbons meet. See `WAVEFORM_LAYOUT_MIRRORED`. */
  readonly orientation: WaveformOrientation;
  readonly path?: RenderPath;
}

export interface WaveformHost {
  readonly path: RenderPath;
  /** Frame statistics, in the render worker's layout so `/bench` reads both with one function. */
  readonly stats: SharedArrayBuffer;
  /** Why the ribbon never started, or null if it did (P4.15). See `RendererHost.startupError`. */
  readonly startupError: string | null;
  setTrack(
    mipmaps: SharedArrayBuffer,
    signals: SharedArrayBuffer,
    frameCount: number,
    sampleRateHz: number,
  ): void;
  /**
   * The beatgrid to tick, or null (P3.13).
   *
   * Separate from `setTrack` because the two change at different rates: a track arrives once and
   * FR-13's tools can move its grid repeatedly without any audio changing. Folding it in would mean
   * re-uploading megabytes of mipmap to move a line by ten milliseconds.
   */
  setGrid(grid: WaveformGrid | null): void;
  clearTrack(): void;
  resize(width: number, height: number, pixelRatio: number): void;
  dispose(): void;
}

class WorkerWaveformHost implements WaveformHost {
  readonly path = 'worker' as const;
  readonly stats = new SharedArrayBuffer(RENDER_STATS_BYTES);
  readonly #worker: Worker;
  #startupError: string | null = null;

  get startupError(): string | null {
    return this.#startupError;
  }

  constructor(options: WaveformHostOptions) {
    this.#worker = new Worker(WAVEFORM_WORKER_URL, {
      type: 'module',
      name: 'synapse-waveform',
    });

    this.#worker.addEventListener('message', (event: MessageEvent<RenderWorkerEvent>) => {
      // `RenderWorkerEvent` has one member today, so the discriminant reads as always-true and the
      // linter says so. It is checked anyway: a second event type added later would otherwise be
      // treated as a startup failure in silence, and this listener exists because of a failure that
      // was silent.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (event.data.type === 'startup-failed') {
        this.#startupError = event.data.message;
      }
    });
    this.#worker.addEventListener('error', (event: ErrorEvent) => {
      this.#startupError ??= event.message || 'the waveform worker failed to load';
    });

    const offscreen = options.canvas.transferControlToOffscreen();
    this.#post(
      {
        type: 'init',
        canvas: offscreen,
        stats: this.stats,
        width: options.width,
        height: options.height,
        pixelRatio: options.pixelRatio,
        theme: options.theme,
        sourceIndex: options.sourceIndex,
        orientation: options.orientation,
      },
      [offscreen],
    );
  }

  #post(message: WaveformWorkerMessage, transfer: Transferable[] = []): void {
    this.#worker.postMessage(message, transfer);
  }

  setTrack(
    mipmaps: SharedArrayBuffer,
    signals: SharedArrayBuffer,
    frameCount: number,
    sampleRateHz: number,
  ): void {
    // No transfer list: both are shared. Transferring the signal ring would detach the audio
    // thread's own publishing destination.
    this.#post({ type: 'track', mipmaps, signals, frameCount, sampleRateHz });
  }

  setGrid(grid: WaveformGrid | null): void {
    this.#post({ type: 'grid', grid });
  }

  clearTrack(): void {
    this.#post({ type: 'clear' });
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.#post({ type: 'resize', width, height, pixelRatio });
  }

  dispose(): void {
    this.#post({ type: 'dispose' });
    this.#worker.terminate();
  }
}

/** The fallback. Same renderer, same loop, same stats — just on this thread. */
class MainThreadWaveformHost implements WaveformHost {
  readonly path = 'main-thread' as const;
  readonly stats = new SharedArrayBuffer(RENDER_STATS_BYTES);
  readonly #renderer: WaveformRenderer | null = null;
  readonly #loop: RenderLoop | null = null;
  readonly startupError: string | null = null;

  constructor(options: WaveformHostOptions) {
    const { canvas, width, height, pixelRatio } = options;
    // Caught rather than thrown, so both paths fail the same way — see the render host, which took
    // the same change for the same reason. A context this browser will not give is not a Tier-2
    // question: the 11 September failure was Chrome on this project's own laptop.
    try {
      this.#renderer = new WaveformRenderer(
        { canvas, width, height, pixelRatio },
        options.theme,
        options.sourceIndex,
        options.orientation,
      );
      this.#loop = new RenderLoop(this.#renderer, this.stats);
      this.#loop.start();
    } catch (cause) {
      this.startupError = cause instanceof Error ? cause.message : String(cause);
    }
  }

  setTrack(
    mipmaps: SharedArrayBuffer,
    signals: SharedArrayBuffer,
    frameCount: number,
    sampleRateHz: number,
  ): void {
    this.#renderer?.setTrack(mipmaps, signals, frameCount, sampleRateHz);
  }

  setGrid(grid: WaveformGrid | null): void {
    this.#renderer?.setGrid(grid);
  }

  clearTrack(): void {
    this.#renderer?.clearTrack();
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.#renderer?.resize(width, height, pixelRatio);
  }

  dispose(): void {
    this.#loop?.stop();
    this.#renderer?.dispose();
  }
}

export function createWaveformHost(options: WaveformHostOptions): WaveformHost {
  const path = options.path ?? (supportsOffscreenCanvas() ? 'worker' : 'main-thread');
  return path === 'worker'
    ? new WorkerWaveformHost(options)
    : new MainThreadWaveformHost(options);
}
